import PlannerMonth, { PlannerMonthProps } from '@oberoninternal/travelbase-ds/components/calendar/PlannerMonth';
import Period from '@oberoninternal/travelbase-ds/entities/Period';
import { BoxProps } from '@rebass/grid';
import addDays from 'date-fns/addDays';
import isSameMonth from 'date-fns/isSameMonth';
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { PlannerTripFragment } from '../generated/graphql';
import usePlannerModifier from '../hooks/planner/usePlannerModifier';

interface Props extends PlannerMonthProps {
    trips: PlannerTripFragment[];
    showPricePerNight?: boolean;
}

const MemoizedPlannerMonth = ({ trips, period, showPricePerNight, ...props }: Props) => {
    const { date } = props;
    // We only care about trips that are in the month
    const relevantTrips = useMemo(() => {
        const foundTrips: Map<string, PlannerTripFragment[]> = new Map();
        for (const trip of trips) {
            if (
                isSameMonth(new Date(trip.date), date) ||
                isSameMonth(addDays(new Date(trip.date), trip.duration), date)
            ) {
                const dayTrips = foundTrips.get(trip.date) ?? [];
                foundTrips.set(trip.date, [...dayTrips, trip]);
            }
        }
        return foundTrips;
    }, [date, trips]);

    const computeDayProps = usePlannerModifier(
        { period, selecting: props.isSelecting },
        relevantTrips,
        showPricePerNight
    );

    return <StyledPlannerMonth {...props} computeDayProps={computeDayProps} />;
};

const isMonthRelevant = (period: Period, date: Date) =>
    period?.start && (isSameMonth(period.start, date) || (period?.end && isSameMonth(period.end, date)));

type PlannerMonthPropsKeys = keyof Omit<Props, keyof Omit<BoxProps, 'onLoad'>>;

export default memo(MemoizedPlannerMonth, (prev, next) => {
    if (+prev.date !== +next.date) {
        return false;
    }

    if (prev.loading && next.loading) {
        return true;
    }

    if (isMonthRelevant(next.period, next.date) || isMonthRelevant(prev.period, prev.date)) {
        return false;
    }

    // Shallow compare everything else
    // Note we're ignoring computeDayProps and clickHandlers as they could cause expensive rerenders
    // and they're callbacks anyway
    const ignore: Array<PlannerMonthPropsKeys> = [
        'date',
        'onDayClick',
        'computeDayProps',
        'onDayMouseEnter',
        'onDayMouseLeave',
        'period',
        'periodColor',
    ];

    return (Object.keys(next) as Array<PlannerMonthPropsKeys>)
        .filter(key => !ignore.includes(key))
        .every(key => Object.is(prev[key], next[key]));
});

const StyledPlannerMonth = styled(PlannerMonth)`
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: calc(50% - (var(--monthGap) * 1px) / 2);
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
        width: calc(100% / 3 - (var(--monthGap) * 1px * 2) / 3);
    }
`;
